import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { SidebarLink } from './SidebarLink';
import { DepartmentDropdown } from './DepartmentDropdown';
import { Department, sidebarConfigs, departmentOrder, getDepartmentFromPath } from './sidebar-config';
import type { Link } from './sidebar-config';
import { isHiddenWithoutAccess } from './sidebar-config';
import { useMyQuizzesStore, selectOpenCount } from '@/quizzes-react/store/myQuizzesStore';
import { showMyQuizzesLink } from '@/quizzes-react/utils/myQuizzesLink';
import { useAllPageAccess } from '@/shared';
import { useFavoritePages } from '../../hooks/useFavoritePages';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useFirebaseStore } from '../../store/firebaseStore';

const SIDEBAR_PINNED_KEY = 'sidebar-pinned';
const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';

interface SidebarProps {
  department: Department;
  userEmail?: string | null;
}

/**
 * Custom hook to track viewport size category
 * Returns: 'mobile' (<768px), 'narrow' (768-1500px), 'wide' (>1500px)
 */
function useViewportCategory(): 'mobile' | 'narrow' | 'wide' {
  const [category, setCategory] = useState<'mobile' | 'narrow' | 'wide'>(() => {
    if (typeof window === 'undefined') return 'wide';
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1500) return 'narrow';
    return 'wide';
  });

  useEffect(() => {
    const checkViewport = () => {
      const width = window.innerWidth;
      if (width < 768) setCategory('mobile');
      else if (width < 1500) setCategory('narrow');
      else setCategory('wide');
    };

    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  return category;
}

export const Sidebar: React.FC<SidebarProps> = ({ department, userEmail }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(SIDEBAR_PINNED_KEY) === 'true';
  });
  const [isCollapsedWide, setIsCollapsedWide] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  });
  const location = useLocation();
  const currentPath = location.pathname;
  const viewportCategory = useViewportCategory();

  // Local department state — initialized from route prop, synced on URL changes
  const [activeDepartment, setActiveDepartment] = useState<Department>(department);
  const prevPathRef = useRef(currentPath);

  // Auto-sync from URL only on actual navigation (not dropdown selection)
  useEffect(() => {
    if (currentPath !== prevPathRef.current) {
      prevPathRef.current = currentPath;
      const urlDept = getDepartmentFromPath(currentPath);
      if (urlDept) setActiveDepartment(urlDept);
    }
  }, [currentPath]);

  const config = sidebarConfigs[activeDepartment];
  const openQuizCount = useMyQuizzesStore(selectOpenCount);
  const quizGrants = useFirebaseStore((s) => s.currentUser?.pagePermissions);

  // Load user's page access permissions
  const { accessiblePages } = useAllPageAccess(userEmail);

  // Load user's favorite pages
  const { isFavorite, favoritePages } = useFavoritePages();

  // Collect favorited links from ALL departments (cross-department favorites)
  const favoriteLinks = useMemo(() => {
    if (favoritePages.length === 0) return [];
    const links: (Link & { department: Department })[] = [];
    departmentOrder.forEach((dept) => {
      const deptConfig = sidebarConfigs[dept];
      deptConfig.links.forEach((link) => {
        if (isFavorite(link.href) && !link.isPlaceholder && !isHiddenWithoutAccess(link, accessiblePages)) {
          links.push({ ...link, department: dept });
        }
      });
    });
    return links;
  }, [favoritePages, isFavorite, accessiblePages]);

  // Set of favorited hrefs for quick exclusion from Tools list
  const favoriteHrefs = useMemo(
    () => new Set(favoriteLinks.map((l) => l.href)),
    [favoriteLinks],
  );

  // Active department links, excluding favorited ones
  const toolLinks = useMemo(
    () =>
      config.links.filter(
        (link) => !favoriteHrefs.has(link.href) && !isHiddenWithoutAccess(link, accessiblePages),
      ),
    [config.links, favoriteHrefs, accessiblePages],
  );

  // Persist pin state
  useEffect(() => {
    localStorage.setItem(SIDEBAR_PINNED_KEY, String(isPinned));
  }, [isPinned]);

  // Persist wide collapse state
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsedWide));
  }, [isCollapsedWide]);

  const closeSidebar = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleSidebar = useCallback(() => {
    if (viewportCategory === 'wide') {
      setIsCollapsedWide((prev) => !prev);
    } else {
      setIsOpen((prev) => !prev);
    }
  }, [viewportCategory]);

  const togglePin = useCallback(() => {
    setIsPinned((prev) => !prev);
  }, []);

  // Determine if sidebar should be visible
  // Wide desktop: visible unless user collapsed it
  // Narrow desktop: visible if pinned OR open
  // Mobile: visible if open
  const isVisible =
    (viewportCategory === 'wide' && !isCollapsedWide) ||
    (viewportCategory === 'narrow' && (isPinned || isOpen)) ||
    (viewportCategory === 'mobile' && isOpen);

  // Determine if we should show overlay (mobile always, narrow desktop only when not pinned)
  const showOverlay =
    isOpen && (viewportCategory === 'mobile' || (viewportCategory === 'narrow' && !isPinned));

  // Escape closes the overlay sidebar (only while it's actually an overlay)
  useEscapeKey(closeSidebar, showOverlay);

  // Expose toggle function and state globally for GlobalHeader to use
  useEffect(() => {
    window.toggleSidebar = toggleSidebar;
    window.sidebarState = { isPinned, isCollapsedWide, viewportCategory };
    return () => {
      delete window.toggleSidebar;
      delete window.sidebarState;
    };
  }, [toggleSidebar, isPinned, isCollapsedWide, viewportCategory]);

  // Determine positioning: sticky when wide (and not collapsed) OR (narrow AND pinned), fixed otherwise
  const useSticky = (viewportCategory === 'wide' && !isCollapsedWide) || (viewportCategory === 'narrow' && isPinned);

  // Set CSS variable --sidebar-width for fixed header positioning
  // When sidebar is sticky (takes space in layout), set to sidebar width
  // When sidebar is fixed/hidden, set to 0
  useEffect(() => {
    const sidebarWidth = useSticky ? '256px' : '0px';
    document.documentElement.style.setProperty('--sidebar-width', sidebarWidth);
  }, [useSticky]);

  return (
    <>
      {/* Overlay - shown on mobile and narrow desktop (when not pinned) */}
      {showOverlay && (
        <div
          id="sidebar-overlay"
          className="fixed inset-0 bg-scrim/50 z-30"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        id="sidebar"
        className={`w-64 h-screen bg-chrome-deep text-ink-chrome-muted flex flex-col z-40 transform transition-transform duration-300 ease-in-out flex-shrink-0 ${
          useSticky ? 'sticky top-0' : 'fixed inset-y-0 left-0'
        } ${isVisible ? 'translate-x-0' : '-translate-x-full'}`}
      >
        {/* Header */}
        {/* Tight padding so "BOTR Tools" fits beside the pin + close buttons
            (at px-4 / mr-3 / p-2 it truncated to "BOTR To…") */}
        <div className="h-20 flex items-center justify-between gap-2 px-3 bg-chrome-strong flex-shrink-0">
          <div className="flex items-center min-w-0">
            <i className="fa-solid fa-toolbox text-xl mr-2 text-ink-faint flex-shrink-0" aria-hidden="true"></i>
            <h1 className="text-xl font-bold truncate">BOTR Tools</h1>
          </div>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            {/* Collapse button - show on wide desktop */}
            {viewportCategory === 'wide' && (
              <button
                onClick={() => setIsCollapsedWide(true)}
                className="p-1.5 text-ink-faint hover:text-ink-chrome transition-colors"
                title="Collapse sidebar"
                aria-label="Collapse sidebar"
              >
                <i className="fa-solid fa-angles-left text-lg" aria-hidden="true"></i>
              </button>
            )}
            {/* Pin button - only show on narrow desktop */}
            {viewportCategory === 'narrow' && (
              // Pinned: upright accent tack on a filled chip (a "pressed" toggle).
              // Unpinned: faint tack tipped 45°. FA 6.4 Free has no thumbtack-slash.
              <button
                onClick={togglePin}
                className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors ${
                  isPinned
                    ? 'bg-chrome text-accent-light hover:text-ink-chrome'
                    : 'text-ink-faint hover:text-ink-chrome'
                }`}
                title={isPinned ? 'Unpin sidebar' : 'Pin sidebar open'}
                aria-label={isPinned ? 'Unpin sidebar' : 'Pin sidebar open'}
                aria-pressed={isPinned}
              >
                <i className={`fa-solid fa-thumbtack text-base ${isPinned ? '' : 'rotate-45'}`} aria-hidden="true"></i>
              </button>
            )}
            {/* Close button - show on mobile and narrow desktop (when not pinned) */}
            {(viewportCategory === 'mobile' || (viewportCategory === 'narrow' && !isPinned)) && (
              <button
                id="close-sidebar-btn"
                className="w-8 h-8 flex items-center justify-center text-ink-faint hover:text-ink-chrome"
                onClick={closeSidebar}
                aria-label="Close sidebar"
                title="Close sidebar"
              >
                <i className="fa-solid fa-times text-xl" aria-hidden="true"></i>
              </button>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-grow p-4 space-y-4 overflow-y-auto">
          {/* Home Link */}
          <ul className="space-y-1">
            <SidebarLink
              link={{ label: 'Return Home', href: '/', icon: 'fa-home' }}
              isActive={currentPath === '/'}
              onNavigate={closeSidebar}
              iconColor="text-accent-light"
            />
            {showMyQuizzesLink(openQuizCount, quizGrants) && (
              <SidebarLink
                link={{
                  label: 'My Quizzes',
                  href: '/quiz',
                  icon: 'fa-clipboard-question',
                }}
                badgeCount={openQuizCount}
                isActive={currentPath === '/quiz' || currentPath.startsWith('/quiz/')}
                onNavigate={closeSidebar}
                iconColor="text-accent-light"
              />
            )}
          </ul>

          {/* Favorites - cross-department favorited pages */}
          {favoriteLinks.length > 0 && (
            <div>
              <h2 className="text-xs font-semibold text-favorite uppercase tracking-wider mb-2 px-3">
                <i className="fa-solid fa-star mr-1.5" />Favorites
              </h2>
              <ul className="space-y-1">
                {favoriteLinks.map((link) => {
                  const hasAccess = link.isExternal || !link.pageId || accessiblePages.has(link.pageId);
                  const isActive = currentPath === link.href || currentPath.startsWith(link.href + '/');

                  return (
                    <div key={link.href} data-department={link.department}>
                      <SidebarLink
                        link={link}
                        isActive={isActive}
                        onNavigate={closeSidebar}
                        iconColor="text-accent-light"
                        hasAccess={hasAccess}
                        isFavorited={true}
                      />
                    </div>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Department Dropdown */}
          <div>
            <h2 className="text-xs font-semibold text-ink-faint uppercase tracking-wider mb-2 px-3">
              Department
            </h2>
            <DepartmentDropdown
              activeDepartment={activeDepartment}
              onDepartmentChange={setActiveDepartment}
            />
          </div>

          {/* Main Links (excluding favorited pages) */}
          <div>
            <h2 className="text-xs font-semibold text-ink-faint uppercase tracking-wider mb-2 px-3">
              Tools
            </h2>
            <ul className="space-y-1">
              {toolLinks.map((link, index) => {
                const hasAccess = link.isExternal || !link.pageId || accessiblePages.has(link.pageId);
                const isActive = currentPath === link.href || currentPath.startsWith(link.href + '/');

                return (
                  <SidebarLink
                    key={index}
                    link={link}
                    isActive={isActive}
                    onNavigate={closeSidebar}
                    iconColor="text-accent-light"
                    hasAccess={hasAccess}
                  />
                );
              })}
            </ul>
          </div>

          {/* External Links */}
          {config.externalLinks && config.externalLinks.length > 0 && (
            <div>
              <h2 className="text-xs font-semibold text-ink-faint uppercase tracking-wider mb-2 px-3">
                External Links
              </h2>
              <ul className="space-y-1">
                {config.externalLinks.map((link, index) => (
                  <SidebarLink
                    key={index}
                    link={link}
                    isActive={false}
                    onNavigate={closeSidebar}
                    iconColor="text-accent-light"
                    hasAccess={true} // External links always accessible
                  />
                ))}
              </ul>
            </div>
          )}
        </nav>
      </aside>
    </>
  );
};
